import {
  buildOrderedPublicProviderListRows,
  EnhancedSeoHead,
  generateBreadcrumbSchema,
  generateItemListSchema,
  NotFoundPage,
  ProviderList,
  publicListSeoState,
} from "@appcaire/shared/seo";
import { Info, ListFilter, Search } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ConversionCtaBand } from "../components/ConversionCtaBand";
import {
  type StockholmFilterType,
  useStockholmEventTracking,
} from "../lib/analytics";
import { stockholmProviderCanonicalUrl } from "../lib/env";
import { STOCKHOLM_LIST_COPY } from "../lib/list-copy";
import {
  formatSourceDate,
  useStockholmMunicipality,
  useStockholmProviders,
} from "../lib/providers";
import { listPageHref, pageSuffix } from "../lib/seo-text";
import { OFFICIAL_STOCKHOLM_CHOOSER_URL, SITE_ORIGIN } from "../lib/site";
import { ShortlistButton } from "../shortlist/ShortlistButton";
import { DataUnavailable, PageLoading } from "./PageStates";

function average(values: Array<number | null | undefined>): number | null {
  const valid = values.filter(
    (value): value is number =>
      typeof value === "number" && Number.isFinite(value),
  );
  return valid.length === 0
    ? null
    : valid.reduce((sum, value) => sum + value, 0) / valid.length;
}

const DEFAULT_PAGE_SIZE = 25;
const CAIRE_INDEX_METHODOLOGY_URL =
  "https://sverigeshemtjanst.se/topplistor/metodik";
const buildStockholmProviderHref = (slug: string) =>
  `/utforare/${encodeURIComponent(slug)}`;

const finderSeoState = (searchParams: URLSearchParams, maxPage: number) =>
  publicListSeoState(searchParams, SITE_ORIGIN, "/hitta-hemtjanst", {
    maxPage,
  });

export default function FinderPage() {
  const providerQuery = useStockholmProviders();
  const municipalityQuery = useStockholmMunicipality();
  const [searchParams] = useSearchParams();
  const trackStockholmEvent = useStockholmEventTracking();
  const filterState = searchParams.toString();
  const previousFilterState = useRef(filterState);

  useEffect(() => {
    if (previousFilterState.current === filterState) return;
    previousFilterState.current = filterState;

    const filterParams: Array<[string, StockholmFilterType]> = [
      ["q", "search"],
      ["stadsdel", "district"],
      ["regi", "organizational_form"],
      ["sprak", "language"],
      ["kompetens", "competence"],
      ["underleverantor", "subcontractor"],
      ["betyg", "sort"],
      ["sort", "sort"],
    ];
    const filterType = filterParams.find(([key]) => searchParams.has(key))?.[1];
    if (!filterType) return;
    const rawSort = searchParams.get("sort");
    const sortId =
      rawSort === "name" ||
      rawSort === "caireIndex" ||
      rawSort === "satisfaction" ||
      rawSort === "continuity" ||
      rawSort === "relevance"
        ? rawSort
        : undefined;
    trackStockholmEvent("filter_apply", {
      filter_type: filterType,
      sort_id: sortId,
      source_surface: "finder",
    });
  }, [filterState, searchParams, trackStockholmEvent]);

  if (providerQuery.loading || municipalityQuery.loading)
    return <PageLoading label="Hämtar utförare…" />;
  if (
    providerQuery.error ||
    municipalityQuery.error ||
    providerQuery.providers.length === 0
  ) {
    return (
      <DataUnavailable
        path="/hitta-hemtjanst"
        title="Utförarlistan är tillfälligt otillgänglig"
      />
    );
  }

  const providers = providerQuery.providers;
  const orderedRows = buildOrderedPublicProviderListRows(providers, {
    municipalitySlug: "stockholm",
    municipalityHref: "/hitta-hemtjanst",
    providerHrefBuilder: buildStockholmProviderHref,
    groupHrefBuilder: false,
  });
  const maximumPage = Math.max(
    1,
    Math.ceil(orderedRows.length / DEFAULT_PAGE_SIZE),
  );
  const { page, cleanPagination, indexable, canonicalUrl } = finderSeoState(
    searchParams,
    maximumPage,
  );
  if (cleanPagination && page > maximumPage) {
    return (
      <NotFoundPage
        title="Sidan finns inte i utförarlistan"
        description={`Utförarlistan har ${maximumPage} sidor. Välj en befintlig sida eller börja om från första sidan.`}
        navigationLinks={[
          { label: "Första sidan", to: "/hitta-hemtjanst" },
          { label: "Alla stadsdelar", to: "/stadsdelar" },
          { label: "Startsidan", to: "/" },
        ]}
      />
    );
  }
  const municipality = municipalityQuery.data?.municipality;
  const listSource = {
    __typename: "MunicipalProviderListSource" as const,
    tier: municipality?.providerListSource?.tier ?? "live",
    url:
      municipality?.providerListSource?.url ?? OFFICIAL_STOCKHOLM_CHOOSER_URL,
  };
  const sourceDate = formatSourceDate(providerQuery.sourceDate);
  const pageTitle =
    page > 1
      ? `Hitta hemtjänst i Stockholm – sida ${page}`
      : "Hitta hemtjänst i Stockholm – jämför utförare";
  const pageRows = orderedRows.slice(
    (page - 1) * DEFAULT_PAGE_SIZE,
    page * DEFAULT_PAGE_SIZE,
  );
  const schemaMarkup = [
    generateBreadcrumbSchema([
      { name: "Hem", url: SITE_ORIGIN },
      { name: "Hitta hemtjänst", url: `${SITE_ORIGIN}/hitta-hemtjanst` },
    ]),
    ...(indexable
      ? [
          generateItemListSchema({
            name: "Hemtjänstutförare i Stockholm",
            url: canonicalUrl,
            items: pageRows.map((row, index) => ({
              name: row.name,
              url: stockholmProviderCanonicalUrl(row.slug),
              position: (page - 1) * DEFAULT_PAGE_SIZE + index + 1,
            })),
          }),
        ]
      : []),
  ];

  return (
    <>
      <EnhancedSeoHead
        siteKey="stockholm"
        title={pageTitle}
        description={`Sök och filtrera bland ${orderedRows.length} hemtjänstutförare i Stockholm. Jämför stadsdelar, kommunal eller privat, språk och kvalitet från officiella källor.${pageSuffix(page, maximumPage)}`}
        canonicalUrl={canonicalUrl}
        indexable={indexable}
        keywords={[
          "hitta hemtjänst Stockholm",
          "hemtjänstutförare Stockholm",
          "utförare hemtjänst Stockholm",
        ]}
        contentClassification="Consumer provider directory"
        targetAudience="seniors, relatives, Stockholm residents"
        schemaMarkup={schemaMarkup}
      />

      <header className="border-b border-border bg-accent/65">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand-secondary">
            <Search className="h-4 w-4" aria-hidden="true" /> Alla utförare i
            Stockholm
          </p>
          <h1 className="mt-3 font-[var(--font-editorial)] text-4xl font-bold tracking-tight text-foreground md:text-5xl">
            Hitta hemtjänst i Stockholm
          </h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-muted-foreground">
            Sök bland alla godkända hemtjänstutförare i Stockholm. Filtrera på
            stadsdel, kommunal eller privat, språk, särskild kompetens och om de
            anlitar underleverantörer.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-sm text-muted-foreground">
            <span className="rounded-full border border-brand-primary/20 bg-white px-4 py-2 font-semibold text-brand-primary">
              {orderedRows.length} utförare
            </span>
            <span className="rounded-full border border-border bg-white px-4 py-2">
              Uppdaterad: {sourceDate}
            </span>
            <span className="rounded-full border border-border bg-white px-4 py-2">
              Senast kontrollerad: {formatSourceDate(providerQuery.crawlDate)}
            </span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <aside className="mb-8 flex gap-3 rounded-2xl border border-brand-primary/20 bg-brand-primary/5 p-5 text-sm leading-6 text-muted-foreground">
          <Info
            className="mt-0.5 h-5 w-5 shrink-0 text-brand-primary"
            aria-hidden="true"
          />
          <p>
            <strong className="text-foreground">
              Kontrollera att utföraren kan komma hem till dig.
            </strong>{" "}
            En utförare arbetar i en stadsdel, men har inte alltid plats för nya
            just nu. Ring och fråga om de kan börja på din adress och vid de
            tider du behöver. Saknas en kvalitetssiffra skriver vi det – aldrig
            en nolla. CaireIndex och den nationella placeringen är ett separat,
            transparent jämförelsestöd och ändrar inte den förvalda alfabetiska
            ordningen.{" "}
            <a
              href={CAIRE_INDEX_METHODOLOGY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackStockholmEvent("seo_funnel_click", {
                  source_surface: "finder",
                  target_path: "/topplistor/metodik",
                })
              }
              className="inline-flex min-h-11 items-center font-semibold text-brand-primary underline underline-offset-4"
            >
              Läs hur indexet beräknas
            </a>
            .
          </p>
        </aside>

        <ProviderList
          providers={providers}
          stats={{
            totalProviders: orderedRows.length,
            filteredCount: orderedRows.length,
            avgSatisfaction: average(
              orderedRows.map((row) => row.satisfaction),
            ),
            avgContinuity: average(orderedRows.map((row) => row.continuity)),
          }}
          municipalitySlug="stockholm"
          municipalityHref="/hitta-hemtjanst"
          copy={STOCKHOLM_LIST_COPY}
          providerHrefBuilder={buildStockholmProviderHref}
          groupHrefBuilder={false}
          listSource={listSource}
          presentation="consumer"
          showCaireIndex
          ranks={{
            municipality: { label: "Stockholms stad" },
            national: { label: "Nationellt" },
          }}
          caireIndexMethodologyHref={CAIRE_INDEX_METHODOLOGY_URL}
          renderProviderActions={(row) => <ShortlistButton row={row} />}
          paginationHrefBuilder={(nextPage, pageSize) =>
            listPageHref("/hitta-hemtjanst", searchParams, nextPage, pageSize)
          }
        />

        <nav
          aria-label="Fortsätt jämförelsen"
          className="mt-10 grid gap-4 sm:grid-cols-3"
        >
          <Link
            to="/stadsdelar"
            onClick={() =>
              trackStockholmEvent("seo_funnel_click", {
                source_surface: "finder",
                target_path: "/stadsdelar",
              })
            }
            className="flex min-h-20 items-center gap-4 rounded-2xl border border-border bg-card p-5 font-semibold text-foreground hover:border-brand-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          >
            <ListFilter
              className="h-6 w-6 text-brand-secondary"
              aria-hidden="true"
            />{" "}
            Hitta via stadsdel
          </Link>
          <Link
            to="/guider/valja-hemtjanst"
            onClick={() =>
              trackStockholmEvent("seo_funnel_click", {
                source_surface: "finder",
                target_path: "/guider/valja-hemtjanst",
              })
            }
            className="flex min-h-20 items-center gap-4 rounded-2xl border border-border bg-card p-5 font-semibold text-foreground hover:border-brand-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          >
            <Info className="h-6 w-6 text-brand-secondary" aria-hidden="true" />{" "}
            Så väljer du utförare i Stockholm
          </Link>
          <Link
            to="/guider/checklista-hemtjanstutforare"
            className="flex min-h-20 items-center gap-4 rounded-2xl border border-border bg-card p-5 font-semibold text-foreground hover:border-brand-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          >
            <Info className="h-6 w-6 text-brand-secondary" aria-hidden="true" />{" "}
            Checklista innan du kontaktar
          </Link>
        </nav>
        <ConversionCtaBand
          sourceSurface="finder"
          showPrimary={false}
          title="Kontakta en utförare eller Caire"
          description="Öppna en utförarprofil i listan och skicka förfrågan, eller kontakta Caire om du har frågor om guiden."
        />
      </div>
    </>
  );
}
